// Frontend session expiry, driven by the `expiry_timestamp` returned at
// login (event/booking/verify-otp) with the JWT `exp` claim as a fallback
// for login flows that don't return one. The expiry is persisted next to the
// auth token and enforced by useSessionExpiry (app-level timer) and useAxios
// (pre-request check), so the user is signed out the moment the session
// lapses instead of waiting for the next 401 from the backend.

const SESSION_EXPIRY_KEY = "sessionExpiry";
const SESSION_EXPIRED_FLAG = "sessionExpired";

// The auth token effectively in use (guard token wins, mirroring
// useAuthRedirect / signIn).
export function getStoredAuthToken() {
  return (
    sessionStorage.getItem("auth_token_with_guard") ||
    localStorage.getItem("auth_token_with_guard") ||
    sessionStorage.getItem("authToken") ||
    localStorage.getItem("authToken") ||
    ""
  );
}

function decodeTokenExpiryMs(token) {
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
    const { exp } = JSON.parse(atob(padded));
    return exp ? Math.round(exp * 1000) : null;
  } catch (error) {
    return null;
  }
}

// Persist the session expiry whenever a token is stored. `expiryTimestamp`
// is the epoch-seconds value from the API (e.g. 1783122105.83); when absent
// the token's own `exp` claim is used. Clears any previous value if neither
// is available so a stale expiry can't sign out a fresh session.
export function setSessionExpiry(expiryTimestamp, authToken) {
  const fromApi = Number(expiryTimestamp);

  const expiryMs =
    fromApi > 0
      ? // Values past ~2001-09 in ms are already milliseconds.
        Math.round(fromApi > 1e12 ? fromApi : fromApi * 1000)
      : decodeTokenExpiryMs(authToken || "");

  if (expiryMs) {
    sessionStorage.setItem(SESSION_EXPIRY_KEY, String(expiryMs));
    localStorage.setItem(SESSION_EXPIRY_KEY, String(expiryMs));
  } else {
    clearSessionExpiry();
  }
}

export function clearSessionExpiry() {
  sessionStorage.removeItem(SESSION_EXPIRY_KEY);
  localStorage.removeItem(SESSION_EXPIRY_KEY);
}

export function getSessionExpiryMs() {
  const stored = Number(
    sessionStorage.getItem(SESSION_EXPIRY_KEY) ||
      localStorage.getItem(SESSION_EXPIRY_KEY)
  );

  if (stored > 0) {
    return stored;
  }

  const token = getStoredAuthToken();
  return token ? decodeTokenExpiryMs(token) : null;
}

// Expired = a token is present AND its expiry is known and in the past. No
// token, or a token with no known expiry, never counts as expired (those
// flows keep today's behaviour of relying on backend 401s).
export function isSessionExpired() {
  if (!getStoredAuthToken()) {
    return false;
  }

  const expiryMs = getSessionExpiryMs();
  return Boolean(expiryMs) && Date.now() >= expiryMs;
}

// The sign-out redirect is a full page load, so an in-memory alert would be
// lost. This flag survives the redirect (deleteAccessToken doesn't clear it)
// and is consumed on the next load to tell the user why they were signed out.
export function markSessionExpired() {
  sessionStorage.setItem(SESSION_EXPIRED_FLAG, "true");
}

export function consumeSessionExpiredFlag() {
  const flagged = sessionStorage.getItem(SESSION_EXPIRED_FLAG) === "true";

  if (flagged) {
    sessionStorage.removeItem(SESSION_EXPIRED_FLAG);
  }

  return flagged;
}
